import React, { useState, useMemo, useEffect, useRef } from 'react';
import PlanCard from './PlanCard';
import { formatCurrency } from '../../utils/currency';
import { getPlanningIntentConfig } from '../../utils/planningIntent';

const PlansView = ({
  plans,
  sharedPlans = [],
  onLoadPlan,
  onDeletePlan,
  onDuplicatePlan,
  onSharePlan,
  onEditName,
  onToggleFavorite,
  onBulkDelete,
  searchQuery = '',
  showShared = false,
  currentFilter, // Optional controlled state
  onFilterChange // Optional controlled handler
}) => {
  const [internalFilter, setInternalFilter] = useState('all'); // all, drafts, active, completed

  // Use controlled state if provided, otherwise internal state
  const filter = currentFilter !== undefined ? currentFilter : internalFilter;
  const setFilter = onFilterChange || setInternalFilter;

  const [sortBy, setSortBy] = useState('date'); // date, name, budget
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [viewMode, setViewMode] = useState('grid'); // grid, list
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const formatPlanBudget = (plan) => {
    const budgetVal = plan?.event_data?.budget;
    if (budgetVal === null || budgetVal === undefined || budgetVal === '' || Number(budgetVal) <= 0) {
      return '-';
    }
    return formatCurrency(budgetVal, plan?.event_data?.country || 'US');
  };

  // Force grid view on small screens
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // Force grid view on screens smaller than 768px (md breakpoint)
      if (window.innerWidth < 768 && viewMode === 'list') {
        setViewMode('grid');
      }
    };

    // Check on mount
    if (windowWidth < 768 && viewMode === 'list') {
      setViewMode('grid');
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode, windowWidth]);
  const [selectedPlans, setSelectedPlans] = useState(new Set());
  const [showActionsMenu, setShowActionsMenu] = useState(null); // plan_id of plan with open menu (for list view)
  const [showCardActionsMenu, setShowCardActionsMenu] = useState(null); // plan_id of plan with open menu (for grid view)
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const menuButtonRefs = useRef({});
  const displayPlans = showShared ? sharedPlans : plans;

  const lastScrollTopRef = useRef(0);

  const preserveScroll = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const main = document.querySelector("main[data-app-main-scroll='true']") || document.querySelector("main");
    if (main) {
      const currentScroll = main.scrollTop;
      if (currentScroll > 0) {
        lastScrollTopRef.current = currentScroll;
      }
      window.requestAnimationFrame(() => {
        if (main) main.scrollTop = currentScroll;
      });
      setTimeout(() => {
        if (main) main.scrollTop = currentScroll;
      }, 50);
      setTimeout(() => {
        if (main) main.scrollTop = currentScroll;
      }, 150);
      setTimeout(() => {
        if (main) main.scrollTop = currentScroll;
      }, 300);
    }
  };

  useEffect(() => {
    if (displayPlans.length > 0) {
      const main = document.querySelector("main[data-app-main-scroll='true']") || document.querySelector("main");
      if (main && lastScrollTopRef.current > 0) {
        const savedScroll = lastScrollTopRef.current;
        main.scrollTop = savedScroll;
        window.requestAnimationFrame(() => {
          if (main) main.scrollTop = savedScroll;
        });
        setTimeout(() => {
          if (main) main.scrollTop = savedScroll;
        }, 50);
        setTimeout(() => {
          if (main) main.scrollTop = savedScroll;
        }, 150);
      }
    }
  }, [displayPlans]);

  // Close actions menu when clicking outside (for list view)
  useEffect(() => {
    if (!showActionsMenu) return;

    let cleanup = null;

    // Add a small delay before attaching the listener to avoid immediate closure
    const timeoutId = setTimeout(() => {
      const handleClickOutside = (e) => {
        // Check if click is on menu button or dropdown
        const isMenuButton = e.target.closest('.actions-menu-container button');
        const isDropdown = e.target.closest('.actions-menu-dropdown');

        // Only close if clicking outside both
        if (!isMenuButton && !isDropdown) {
          setShowActionsMenu(null);
        }
      };

      document.addEventListener('mousedown', handleClickOutside, true);

      cleanup = () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      if (cleanup) cleanup();
    };
  }, [showActionsMenu]);

  // Close card actions menu when clicking outside (for grid view)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showCardActionsMenu) {
        // Check if click is on any card's menu button or dropdown
        const isMenuButton = e.target.closest('.plan-card-actions-menu') || e.target.closest('.plan-card-actions-menu button');
        const isDropdown = e.target.closest('.plan-card-actions-dropdown');

        // Only close if clicking outside both
        if (!isMenuButton && !isDropdown) {
          preserveScroll();
          setShowCardActionsMenu(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCardActionsMenu]);

  // displayPlans is now defined above to satisfy eslint no-use-before-define

  // Handle scroll to close menu or update position
  useEffect(() => {
    if (!showActionsMenu) return;

    const handleScroll = () => {
      const buttonRef = menuButtonRefs.current[showActionsMenu];
      if (buttonRef) {
        const rect = buttonRef.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right
        });
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [showActionsMenu]);

  // Filter plans
  const filteredPlans = useMemo(() => {
    let filtered = [...displayPlans];

    // Status filter
    if (filter === 'drafts') {
      filtered = filtered.filter(p => !p.themes || p.themes.length === 0);
    } else if (filter === 'active') {
      filtered = filtered.filter(p => {
        const hasThemes = p.themes && p.themes.length > 0;
        const hasSelectedTheme = p.selected_theme;
        return hasThemes && !hasSelectedTheme;
      });
    } else if (filter === 'completed') {
      filtered = filtered.filter(p => {
        const hasSelectedTheme = p.selected_theme;
        const hasShoppingList = p.shopping_items && p.shopping_items.length > 0;
        return hasSelectedTheme && hasShoppingList;
      });
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => {
        const eventType = (p.event_data?.event_type || '').toLowerCase();
        return eventType.includes(query);
      });
    }

    return filtered;
  }, [displayPlans, filter, searchQuery]);

  // Sort plans
  const sortedPlans = useMemo(() => {
    const sorted = [...filteredPlans];

    sorted.sort((a, b) => {
      // Favorites always float to the top, regardless of the active sort
      // direction. This keeps starred plans visually pinned even while the
      // user re-sorts by date/name/budget.
      const favA = a.is_favorite ? 1 : 0;
      const favB = b.is_favorite ? 1 : 0;
      if (favA !== favB) return favB - favA;

      let comparison = 0;

      if (sortBy === 'date') {
        comparison = new Date(b.updated_at) - new Date(a.updated_at);
      } else if (sortBy === 'name') {
        const nameA = (a.event_data?.event_type || '').toLowerCase();
        const nameB = (b.event_data?.event_type || '').toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else if (sortBy === 'budget') {
        comparison = (b.event_data?.budget || 0) - (a.event_data?.budget || 0);
      }

      return sortOrder === 'asc' ? -comparison : comparison;
    });

    return sorted;
  }, [filteredPlans, sortBy, sortOrder]);

  const handleSelectPlan = (planId, selected) => {
    const newSelected = new Set(selectedPlans);
    if (selected) {
      newSelected.add(planId);
    } else {
      newSelected.delete(planId);
    }
    setSelectedPlans(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedPlans.size === sortedPlans.length) {
      setSelectedPlans(new Set());
    } else {
      setSelectedPlans(new Set(sortedPlans.map(p => p.plan_id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPlans.size === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedPlans.size} plan${selectedPlans.size !== 1 ? 's' : ''}? This action cannot be undone.`;
    if (window.confirm(confirmMessage)) {
      try {
        // If onBulkDelete callback is provided, use it
        if (onBulkDelete) {
          await onBulkDelete(Array.from(selectedPlans));
          setSelectedPlans(new Set());
          return;
        }

        // Otherwise, delete directly using planUtils
        const planUtils = await import('../../utils/plans');

        // Delete all selected plans
        const deletePromises = Array.from(selectedPlans).map(planId =>
          planUtils.deletePlan(planId).catch(err => {
            console.error(`Failed to delete plan ${planId}:`, err);
            return null; // Continue with other deletions even if one fails
          })
        );

        await Promise.all(deletePromises);

        // Clear selection after deletion
        setSelectedPlans(new Set());

        // Note: Parent component should reload plans after bulk delete
        // If onDeletePlan is provided, we can call it to trigger a reload
        // (though this is a workaround - ideally parent handles reload)
      } catch (error) {
        console.error('Bulk delete error:', error);
        alert('Failed to delete some plans. Please try again.');
      }
    }
  };

  const stats = useMemo(() => {
    const total = displayPlans.length;
    const drafts = displayPlans.filter(p => !p.themes || p.themes.length === 0).length;
    const active = displayPlans.filter(p => {
      const hasThemes = p.themes && p.themes.length > 0;
      const hasSelectedTheme = p.selected_theme;
      return hasThemes && !hasSelectedTheme;
    }).length;
    const completed = displayPlans.filter(p => {
      const hasSelectedTheme = p.selected_theme;
      const hasShoppingList = p.shopping_items && p.shopping_items.length > 0;
      return hasSelectedTheme && hasShoppingList;
    }).length;

    return { total, drafts, active, completed };
  }, [displayPlans]);

  return (
    <div className="space-y-6">
      {/* Filters and Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* Filter Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
          <button
            onClick={() => setFilter('all')}
            className={`
              px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition focus:outline-none focus:ring-0
              ${filter === 'all'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:bg-gray-100'
              }
            `}
          >
            All ({stats.total})
          </button>
          <button
            onClick={() => setFilter('drafts')}
            className={`
              px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition focus:outline-none focus:ring-0
              ${filter === 'drafts'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:bg-gray-100'
              }
            `}
          >
            Drafts ({stats.drafts})
          </button>
          <button
            onClick={() => setFilter('active')}
            className={`
              px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition focus:outline-none focus:ring-0
              ${filter === 'active'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:bg-gray-100'
              }
            `}
          >
            Active ({stats.active})
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`
              px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition focus:outline-none focus:ring-0
              ${filter === 'completed'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:bg-gray-100'
              }
            `}
          >
            Completed ({stats.completed})
          </button>
        </div>

        {/* Sort and View Controls */}
        <div className="flex items-center gap-3">
          {/* Sort */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Sort:</label>
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none appearance-none"
              >
                <option value="date">Date</option>
                <option value="name">Name</option>
                <option value="budget">Budget</option>
              </select>
              {/* Custom chevron icon */}
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition"
              title={sortOrder === 'asc' ? 'Sort descending' : 'Sort ascending'}
            >
              {sortOrder === 'asc' ? (
                // Ascending = double chevron pointing up, center-aligned
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 17l7-7 7 7M5 9l7-7 7 7" />
                </svg>
              ) : (
                // Descending = double chevron pointing down, center-aligned
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-7 7-7-7M19 15l-7 7-7-7" />
                </svg>
              )}
            </button>
          </div>

          {/* View Toggle - Hidden on small screens (force grid view) */}
          <div className="hidden md:flex items-center gap-1 border border-gray-300 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`
                p-1.5 rounded transition focus:outline-none focus:ring-0
                ${viewMode === 'grid'
                  ? 'bg-purple-100 text-purple-700'
                  : 'text-gray-400 hover:text-gray-600'
                }
              `}
              title="Grid view"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`
                p-1.5 rounded transition focus:outline-none focus:ring-0
                ${viewMode === 'list'
                  ? 'bg-purple-100 text-purple-700'
                  : 'text-gray-400 hover:text-gray-600'
                }
              `}
              title="List view"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedPlans.size > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-sm font-medium text-purple-900">
            {selectedPlans.size} plan{selectedPlans.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedPlans(new Set())}
              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-white rounded transition"
            >
              Clear
            </button>
            <button
              onClick={handleSelectAll}
              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-white rounded transition"
            >
              Select All
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1.5 text-sm text-white bg-red-600 hover:bg-red-700 rounded transition flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Plans Grid/List */}
      {sortedPlans.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-gray-300 p-12 text-center">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {searchQuery
              ? 'No plans found'
              : showShared
                ? `No ${filter === 'all' ? '' : filter} shared plans yet`
                : `No ${filter === 'all' ? '' : filter} plans yet`
            }
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {searchQuery
              ? 'Try adjusting your search or filters'
              : showShared
                ? 'Plans shared with you will appear here. Ask someone to share a plan with you!'
                : 'Create your first plan to get started!'
            }
          </p>
        </div>
      ) : viewMode === 'list' ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[320px] sm:min-w-[640px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-2 sm:px-3 py-2 sm:py-3 text-left w-8 sm:w-auto">
                    <input
                      type="checkbox"
                      checked={selectedPlans.size === sortedPlans.length && sortedPlans.length > 0}
                      onChange={handleSelectAll}
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600 border-gray-300 rounded focus:ring-0"
                    />
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <span className="hidden sm:inline">Plan Name</span>
                    <span className="sm:hidden">Name</span>
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden sm:table-cell">Status</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell">Budget</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden lg:table-cell">Guests</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell">Created</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden lg:table-cell">Last Modified</th>
                  <th className="px-2 sm:px-3 py-2 sm:py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider w-12 sm:w-16 hidden sm:table-cell">Menu</th>
                  <th className="px-2 sm:px-3 py-2 sm:py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider w-12 sm:w-16">
                    <span className="sm:hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedPlans.map((plan) => {
                  const planName = plan.event_data?.plan_name || plan.event_data?.event_type || 'Untitled Plan';
                  const hasThemes = plan.themes && plan.themes.length > 0;
                  const selectedTheme = plan.selected_theme;
                  const isDraft = !hasThemes;
                  const intentConfig = getPlanningIntentConfig(plan.event_data?.focus_selection);
                  const isCompleted = selectedTheme && (
                    intentConfig.key === 'theme_ideas' || 
                    (plan.shopping_items && plan.shopping_items.length > 0)
                  );
                  const getPrimaryActionLabel = () => {
                    if (!isCompleted) {
                      if (hasThemes && !selectedTheme) {
                        switch (intentConfig.key) {
                          case 'food': return 'Choose menu concept';
                          case 'decor': return 'Choose decor concept';
                          case 'activities': return 'Choose activity concept';
                          case 'shopping': return 'Choose shopping direction';
                          case 'theme_ideas': return 'Choose concept';
                          case 'mixed': return 'Choose plan concept';
                          case 'everything':
                          default: return 'Choose theme';
                        }
                      }
                      return 'Continue planning';
                    }
                    switch (intentConfig.key) {
                      case 'theme_ideas': return 'View board';
                      case 'decor': return 'View decor plan';
                      case 'food': return 'View food plan';
                      case 'activities': return 'View activities plan';
                      case 'shopping': return 'View shopping list';
                      case 'mixed': return 'View custom plan';
                      case 'everything':
                      default: return 'View plan';
                    }
                  };
                  const primaryActionLabel = getPrimaryActionLabel();
                  const status = isDraft
                    ? 'Draft'
                    : isCompleted
                      ? (intentConfig.key === 'theme_ideas' ? 'Concept Ready' : 'Plan Ready')
                      : (hasThemes && !selectedTheme)
                        ? (intentConfig.key === 'everything' ? 'Themes Ready' : 'Concepts Ready')
                        : 'In Progress';
                  const statusColor = isDraft
                    ? 'bg-gray-100 text-gray-700 border-gray-300'
                    : isCompleted
                      ? 'bg-green-100 text-green-700 border-green-300'
                      : (hasThemes && !selectedTheme)
                        ? 'bg-purple-100 text-purple-700 border-purple-300'
                        : 'bg-amber-100 text-amber-700 border-amber-300';
                  const formatDate = (dateString) => {
                    if (!dateString) return 'N/A';
                    const date = new Date(dateString);
                    const now = new Date();

                    // Get dates at start of day in local timezone for accurate day comparison
                    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                    const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

                    // Calculate difference in calendar days
                    const diffTime = nowStart - dateStart;
                    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

                    if (diffDays === 0) return 'Today';
                    if (diffDays === 1) return 'Yesterday';
                    // For older dates, show actual date and time
                    return date.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
                    }) + ' ' + date.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    });
                  };
                  const formatModifiedDate = (dateString) => {
                    if (!dateString) return 'N/A';
                    const date = new Date(dateString);
                    const now = new Date();

                    // Get dates at start of day in local timezone for accurate day comparison
                    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                    const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

                    // Calculate difference in calendar days
                    const diffTime = nowStart - dateStart;
                    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

                    if (diffDays === 0) return 'Today';
                    if (diffDays === 1) return 'Yesterday';
                    // For older dates, show actual date and time
                    return date.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
                    }) + ' ' + date.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    });
                  };
                  return (
                    <tr
                      key={plan.plan_id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={(e) => {
                        const target = e.target;
                        const clickedButton = target.closest('button');
                        const clickedInput = target.closest('input');
                        const clickedMenuContainer = target.closest('.actions-menu-container');
                        const clickedMenuDropdown = target.closest('.actions-menu-dropdown');

                        // If clicking on any interactive element, don't trigger row click
                        if (clickedButton || clickedInput || clickedMenuContainer || clickedMenuDropdown) {
                          return;
                        }

                        onLoadPlan(plan);
                      }}
                    >
                      <td className="px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedPlans.has(plan.plan_id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectPlan(plan.plan_id, e.target.checked);
                          }}
                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600 border-gray-300 rounded focus:ring-0"
                        />
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 min-w-[120px]">
                        <div className="text-xs sm:text-sm font-medium text-gray-900 whitespace-normal break-words">{planName}</div>
                        <div className="text-xs text-gray-500 sm:hidden mt-0.5">
                          <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full border ${statusColor}`}>
                            {status}
                          </span>
                          {' • '}
                          <span className="text-gray-500">{formatPlanBudget(plan)}</span>
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap hidden sm:table-cell">
                        <span className={`px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold rounded-full border ${statusColor}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap hidden md:table-cell">
                        <div className="text-xs sm:text-sm text-gray-900">{formatPlanBudget(plan)}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap hidden lg:table-cell">
                        <div className="text-xs sm:text-sm text-gray-900">
                          {plan.event_data?.guest_count !== null &&
                          plan.event_data?.guest_count !== undefined &&
                          plan.event_data?.guest_count !== '' &&
                          Number(plan.event_data?.guest_count) > 0
                            ? plan.event_data.guest_count
                            : '-'}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap hidden md:table-cell">
                        <div className="text-xs sm:text-sm text-gray-500">{formatDate(plan.created_at)}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap hidden lg:table-cell">
                        <div className="text-xs sm:text-sm text-gray-500">{formatModifiedDate(plan.updated_at || plan.created_at)}</div>
                      </td>
                      <td
                        className="px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap text-center hidden sm:table-cell"
                      >
                        <div
                          className="relative actions-menu-container inline-block"
                        >
                          <button
                            ref={(el) => {
                              if (el) {
                                menuButtonRefs.current[plan.plan_id] = el;
                              }
                            }}
                            onMouseDown={(e) => {
                              // Capture phase - fires before row onClick
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.nativeEvent.stopImmediatePropagation();
                              if (showActionsMenu === plan.plan_id) {
                                setShowActionsMenu(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setMenuPosition({
                                  top: rect.bottom + 4,
                                  right: window.innerWidth - rect.right
                                });
                                setShowActionsMenu(plan.plan_id);
                              }
                            }}
                            type="button"
                            className="text-gray-400 hover:text-gray-600 p-0.5 sm:p-1 rounded hover:bg-gray-100 transition focus:outline-none focus:ring-0 relative z-10"
                            title="More options"
                          >
                            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                            </svg>
                          </button>
                        </div>
                        {showActionsMenu === plan.plan_id && (
                          <div
                            className="fixed w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[100] actions-menu-dropdown"
                            style={{
                              top: `${menuPosition.top}px`,
                              right: `${menuPosition.right}px`
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {onEditName && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditName(plan);
                                  setShowActionsMenu(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit Name
                              </button>
                            )}
                            {onDuplicatePlan && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDuplicatePlan(plan);
                                  setShowActionsMenu(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Duplicate
                              </button>
                            )}
                            {onSharePlan && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSharePlan(plan);
                                  setShowActionsMenu(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                </svg>
                                Share
                              </button>
                            )}
                            {onDeletePlan && (
                              <>
                                <div className="border-t border-gray-200 my-1"></div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDeletePlan(plan.plan_id, e);
                                    setShowActionsMenu(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1 sm:gap-0">
                          {onLoadPlan && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onLoadPlan(plan);
                              }}
                              className="text-purple-600 hover:text-purple-700 p-1.5 sm:p-2 rounded-lg bg-purple-50 hover:bg-purple-100 border border-purple-200 hover:border-purple-300 shadow-sm hover:shadow transition-all focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
                              title={primaryActionLabel}
                              style={{
                                boxShadow: 'inset 0 1px 0 0 rgba(255, 255, 255, 0.5), 0 1px 2px 0 rgba(0, 0, 0, 0.1)'
                              }}
                            >
                              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          )}
                          {/* On mobile, show menu button here too */}
                          <div className="relative actions-menu-container sm:hidden inline-block">
                            <button
                              ref={(el) => {
                                if (el) menuButtonRefs.current[plan.plan_id] = el;
                              }}
                              onMouseDown={(e) => {
                                // Capture phase - fires before row onClick
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.nativeEvent.stopImmediatePropagation();
                                if (showActionsMenu === plan.plan_id) {
                                  setShowActionsMenu(null);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setMenuPosition({
                                    top: rect.bottom + 4,
                                    right: window.innerWidth - rect.right
                                  });
                                  setShowActionsMenu(plan.plan_id);
                                }
                              }}
                              type="button"
                              className="text-gray-400 hover:text-gray-600 p-0.5 rounded hover:bg-gray-100 transition focus:outline-none focus:ring-0 ml-1"
                              title="More options"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        {showActionsMenu === plan.plan_id && (
                          <div
                            className="fixed w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[100] actions-menu-dropdown"
                            style={{
                              top: `${menuPosition.top}px`,
                              right: `${menuPosition.right}px`
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {onEditName && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditName(plan);
                                  setShowActionsMenu(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit Name
                              </button>
                            )}
                            {onDuplicatePlan && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDuplicatePlan(plan);
                                  setShowActionsMenu(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Duplicate
                              </button>
                            )}
                            {onSharePlan && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSharePlan(plan);
                                  setShowActionsMenu(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                </svg>
                                Share
                              </button>
                            )}
                            {onDeletePlan && (
                              <>
                                <div className="border-t border-gray-200 my-1"></div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDeletePlan(plan.plan_id, e);
                                    setShowActionsMenu(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 justify-items-center sm:justify-items-start">
          {sortedPlans.map((plan) => (
            <div key={plan.plan_id} className="w-full max-w-[280px] sm:max-w-[280px]">
              <PlanCard
                plan={plan}
                onSelect={onLoadPlan}
                onDelete={onDeletePlan}
                onDuplicate={onDuplicatePlan}
                onShare={onSharePlan}
                onEditName={onEditName}
                onToggleFavorite={onToggleFavorite}
                isSelected={selectedPlans.has(plan.plan_id)}
                onSelectChange={handleSelectPlan}
                showActions={showCardActionsMenu === plan.plan_id}
                onToggleActions={(planId) => {
                  preserveScroll();
                  if (planId === null || planId === undefined) {
                    setShowCardActionsMenu(null);
                  } else {
                    setShowCardActionsMenu(showCardActionsMenu === planId ? null : planId);
                  }
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlansView;
